import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
export const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL
  || (API_BASE_URL.startsWith('/')
    ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
    : API_BASE_URL.replace(/^http/i, 'ws').replace(/\/api\/?$/, ''));

export const createAuthenticatedWebSocket = () => {
  const token = localStorage.getItem('token');
  return new WebSocket(WS_BASE_URL, ['smarten-campus', token]);
};

const API = axios.create({
  baseURL: API_BASE_URL,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const entityCode = localStorage.getItem('entity_code');
  if (entityCode) config.headers['X-Entity-Code'] = entityCode;
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (
      err.response?.status === 403
      && /invalid|expired|role not found|token entity/i.test(err.response?.data?.error || '')
    ) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (data) => API.post('/auth/login', data, {
    headers: { 'X-Entity-Code': data.entity_code },
  }),
  getMe: () => API.get('/auth/me'),
  changePassword: (data) => API.post('/auth/change-password', data),
};

export const entitiesAPI = {
  getAll: () => API.get('/entities'),
  getLocaleOptions: () => API.get('/entities/locale-options'),
  create: (data) => API.post('/entities', data),
  update: (id, data) => API.patch(`/entities/${id}`, data),
  resetAdminPassword: (id, password) => API.post(`/entities/${id}/reset-admin-password`, { password }),
};

export const attendanceAPI = {
  getLogs: (params) => API.get('/attendance/logs', { params }),
  getSummary: (params) => API.get('/attendance/summary/today', { params }),
  getAbsences: (params) => API.get('/attendance/absences', { params }),
  getDailyReport: (params) => API.get('/attendance/reports/daily', { params }),
  downloadDailyReport: (params) => API.get('/attendance/reports/daily', {
    params: { ...params, format: 'xlsx' },
    responseType: 'blob',
  }),
  getWorkingHours: (params) => API.get('/attendance/reports/working-hours', { params }),
  getOvertimeRequests: () => API.get('/attendance/overtime'),
  createOvertimeRequest: (data) => API.post('/attendance/overtime', data),
  reviewOvertimeRequest: (id, data) => API.patch(`/attendance/overtime/${id}/review`, data),
  getSectionReport: (params) => API.get('/attendance/reports/section', { params }),
  getStudentPeriodReport: (params) => API.get('/attendance/reports/student-period', { params }),
  getMyAttendanceReport: (params) => API.get('/attendance/me/report', { params }),
  getParentChildren: () => API.get('/attendance/parent/children'),
  getParentChildReport: (params) => API.get('/attendance/parent/child-report', { params }),
  getFaceProfiles: () => API.get('/attendance/face/profiles'),
  getFaceUsers: () => API.get('/attendance/face/users'),
  enrollFaceProfile: (data) => API.post('/attendance/face/enroll', data),
  recordFaceScan: (data) => API.post('/attendance/face/scan', data),
  getPolicies: () => API.get('/attendance/policies'),
  createPolicy: (data) => API.post('/attendance/policies', data),
  updatePolicy: (id, data) => API.patch(`/attendance/policies/${id}`, data),
  getHolidays: () => API.get('/attendance/holidays'),
  createHoliday: (data) => API.post('/attendance/holidays', data),
  updateHoliday: (id, data) => API.patch(`/attendance/holidays/${id}`, data),
  getLeaves: (params) => API.get('/attendance/leaves', { params }),
  createLeave: (data) => API.post('/attendance/leaves', data),
  updateLeave: (id, data) => API.patch(`/attendance/leaves/${id}`, data),
  reviewLeave: (id, data) => API.patch(`/attendance/leaves/${id}/review`, data),
  cancelLeave: (id) => API.patch(`/attendance/leaves/${id}/cancel`),
};

export const usersAPI = {
  getAll: () => API.get('/users'),
  downloadImportTemplate: () => API.get('/users/import-template', { responseType: 'blob' }),
  importWorkbook: (data) => API.post('/users/import', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getById: (id) => API.get(`/users/${id}`),
  create: (data) => API.post('/users', data),
  update: (id, data) => API.patch(`/users/${id}`, data),
  setStatus: (id, isActive) => API.patch(`/users/${id}/status`, { is_active: isActive }),
  restore: (id) => API.patch(`/users/${id}/restore`),
  replaceMobileDevice: (id) => API.delete(`/users/${id}/mobile-device`),
  updateSection: (id, data) => API.patch(`/users/${id}/section`, data),
  assignTag: (data) => API.post('/users/assign-tag', data),
  remove: (id) => API.delete(`/users/${id}`),
};

export const departmentsAPI = {
  getAll: () => API.get('/departments'),
  create: (data) => API.post('/departments', data),
  update: (id, data) => API.patch(`/departments/${id}`, data),
  remove: (id) => API.delete(`/departments/${id}`),
};

export const trackingAPI = {
  getLatestReaderLocations: () => API.get('/readers/locations/latest'),
};

export const subjectsAPI = {
  getAll: () => API.get('/subjects'),
  create: (data) => API.post('/subjects', data),
  update: (id, data) => API.patch(`/subjects/${id}`, data),
};

export const rolesAPI = {
  getAll: () => API.get('/roles'),
  getPermissions: () => API.get('/roles/permissions'),
  create: (data) => API.post('/roles', data),
  update: (id, data) => API.patch(`/roles/${id}`, data),
  remove: (id) => API.delete(`/roles/${id}`),
};

export const classesAPI = {
  getAll: () => API.get('/classes'),
  create: (data) => API.post('/classes', data),
  update: (id, data) => API.patch(`/classes/${id}`, data),
  updateEnrollments: (id, data) => API.patch(`/classes/${id}/enrollments`, data),
  importWorkbook: (id, data) => API.post(`/classes/${id}/import`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  remove: (id) => API.delete(`/classes/${id}`),
};

export const readersAPI = {
  getAll: () => API.get('/readers'),
  create: (data) => API.post('/readers', data),
  update: (id, data) => API.patch(`/readers/${id}`, data),
  updatePin: (id, data) => API.patch(`/readers/${id}/pin`, data),
  remove: (id) => API.delete(`/readers/${id}`),
};

export const camerasAPI = {
  getAll: () => API.get('/cameras'),
  getLiveFrame: (id) => API.get(`/cameras/${id}/live-frame`),
  enrollLiveFaceSample: (id, data) => API.post(`/cameras/${id}/enroll-face-sample`, data),
  create: (data) => API.post('/cameras', data),
  update: (id, data) => API.patch(`/cameras/${id}`, data),
  regenerateToken: (id) => API.post(`/cameras/${id}/regenerate-token`),
  remove: (id) => API.delete(`/cameras/${id}`),
};

export const edgeAgentsAPI = {
  getAll: () => API.get('/edge-agents'),
  create: (data) => API.post('/edge-agents', data),
  regenerateToken: (id) => API.post(`/edge-agents/${id}/regenerate-token`),
  remove: (id) => API.delete(`/edge-agents/${id}`),
  getDiscoveries: () => API.get('/edge-agents/admin/discoveries'),
  enrollDiscovery: (id, data) => API.post(`/edge-agents/admin/discoveries/${id}/enroll`, data),
};

export const cameraAnalyticsAPI = {
  getModules: () => API.get('/camera-analytics/modules'),
  setCameraModules: (cameraId, assignments) => API.put(`/camera-analytics/cameras/${cameraId}/modules`, { assignments }),
  getEvents: (params) => API.get('/camera-analytics/events', { params }),
  reviewEvent: (id, review_status) => API.patch(`/camera-analytics/events/${id}/review`, { review_status }),
};

export const locationsAPI = {
  getAll: () => API.get('/locations'),
  create: (data) => API.post('/locations', data),
  update: (id, data) => API.patch(`/locations/${id}`, data),
  remove: (id) => API.delete(`/locations/${id}`),
};

export const timetableAPI = {
  getAll: () => API.get('/timetable'),
  create: (data) => API.post('/timetable', data),
  update: (id, data) => API.patch(`/timetable/${id}`, data),
  remove: (id) => API.delete(`/timetable/${id}`),
};

export default API;
